import React, { useState, useEffect } from "react";
import { API_CONFIG, ALPHA_GAL_RECIPES } from "../config";

const Kitchen = () => {
  const [ingredients, setIngredients] = useState("");
  const [time, setTime] = useState("");
  const [servings, setServings] = useState("");
  const [avoid, setAvoid] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load favorites from localStorage on component mount
  useEffect(() => {
    const savedFavorites = localStorage.getItem("danaRecipeFavorites");
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error("Error loading favorites:", error);
      }
    }
  }, []);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("danaRecipeFavorites", JSON.stringify(favorites));
  }, [favorites]);

  const searchRecipes = async () => {
    setIsLoading(true);
    setRecipes([]);

    try {
      const searchTerms = ingredients
        .toLowerCase()
        .split(",")
        .map((s) => s.trim())
        .filter((term) => term.length > 0);
      const avoidTerms = avoid
        .toLowerCase()
        .split(",")
        .map((s) => s.trim())
        .filter((term) => term.length > 0);

      console.log("🔍 Search terms:", searchTerms);
      console.log("🚫 Avoid terms:", avoidTerms);

      // Search Alpha-Gal recipes first
      const alphaGalResults = ALPHA_GAL_RECIPES.filter((recipe) => {
        const recipeIngredients = recipe.ingredients.map((ing) =>
          ing.toLowerCase()
        );

        // If no search terms, show all recipes
        if (searchTerms.length === 0) {
          return true;
        }

        const hasMatchingIngredients = searchTerms.some((term) =>
          recipeIngredients.some((ing) => ing.includes(term))
        );

        const hasAvoidedIngredients =
          avoidTerms.length > 0 &&
          avoidTerms.some((term) =>
            recipeIngredients.some((ing) => ing.includes(term))
          );

        console.log(
          `Recipe: ${recipe.title}, hasMatching: ${hasMatchingIngredients}, hasAvoided: ${hasAvoidedIngredients}`
        );

        return hasMatchingIngredients && !hasAvoidedIngredients;
      });

      console.log("🍳 Alpha-Gal results:", alphaGalResults.length);

      let spoonacularResults = [];

      // Try Spoonacular API if enabled
      if (API_CONFIG.SPOONACULAR.enabled && ingredients.trim()) {
        try {
          const response = await fetch(
            `${API_CONFIG.SPOONACULAR.baseUrl}/recipes/complexSearch?apiKey=${
              API_CONFIG.SPOONACULAR.apiKey
            }&includeIngredients=${encodeURIComponent(
              ingredients
            )}&number=12&addRecipeInformation=true&fillIngredients=true&maxReadyTime=${
              time || 120
            }`
          );

          if (response.ok) {
            const data = await response.json();
            spoonacularResults = data.results || [];
            console.log("🥄 Spoonacular results:", spoonacularResults.length);
          } else {
            console.error("Spoonacular API error:", response.status);
          }
        } catch (error) {
          console.error("Spoonacular API error:", error);
        }
      }

      // Combine and deduplicate results
      const allRecipes = [...alphaGalResults, ...spoonacularResults];
      const uniqueRecipes = allRecipes.filter(
        (recipe, index, self) =>
          index === self.findIndex((r) => r.title === recipe.title)
      );

      console.log("🎯 Final results:", uniqueRecipes.length);
      setRecipes(uniqueRecipes.slice(0, 12));
    } catch (error) {
      console.error("Recipe search error:", error);
      setRecipes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFavorite = (recipe) => {
    const isFavorited = favorites.some((fav) => fav.id === recipe.id);

    if (isFavorited) {
      setFavorites((prev) => prev.filter((fav) => fav.id !== recipe.id));
    } else {
      setFavorites((prev) => [...prev, recipe]);
    }
  };

  const removeFavorite = (recipeId) => {
    setFavorites((prev) => prev.filter((fav) => fav.id !== recipeId));
  };

  const viewRecipe = (recipe) => {
    // Create modal for recipe details
    const modal = document.createElement("div");
    modal.className = "modal-overlay";
    modal.innerHTML = `
      <div class="modal-content">
        <button class="modal-close" onclick="this.closest('.modal-overlay').remove()">×</button>
        <img src="${recipe.image}" alt="${recipe.title}" class="modal-image" />
        <h2 class="modal-title">${recipe.title}</h2>
        <p class="modal-time">${recipe.time || "⏰ Time not specified"}</p>
        <p class="modal-summary">${recipe.summary}</p>
        <div class="modal-instructions">
          <h4>🍳 Instructions:</h4>
          <p>${
            recipe.instructions ||
            "This is a delicious Alpha-Gal safe recipe! For detailed cooking instructions, ingredients, and tips, please refer to the original recipe source."
          }</p>
        </div>
        <button class="modal-close-btn" onclick="this.closest('.modal-overlay').remove()">Close</button>
      </div>
    `;

    document.body.appendChild(modal);

    // Close on outside click
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  return (
    <div className="view active">
      <div className="kitchen-header">
        <h2 className="kitchen-title">🍳 Dana's Kitchen</h2>
        <p className="kitchen-subtitle">
          Find Alpha-Gal safe recipes based on your ingredients
        </p>
      </div>

      <div className="kitchen-actions">
        <button
          className="kitchen-action-btn"
          onClick={() => setShowFavorites(false)}
        >
          🔍 Search New Recipes
        </button>
        <button
          className="kitchen-action-btn"
          onClick={() => setShowFavorites(true)}
        >
          ⭐ My Favorites ({favorites.length})
        </button>
      </div>

      {!showFavorites ? (
        <>
          <div className="input-card">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                searchRecipes();
              }}
            >
              <div className="form-group">
                <label className="form-label">
                  🥘 What's in your kitchen, chef?
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="chicken, rice, vegetables..."
                  value={ingredients}
                  onChange={(e) => setIngredients(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  ⏰ How much time you got, busy bee?
                </label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="30"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  👥 How many hungry mouths to feed?
                </label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="4"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  🚫 What's a no-go, girlfriend?
                </label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="dairy, nuts, gluten..."
                  value={avoid}
                  onChange={(e) => setAvoid(e.target.value)}
                />
              </div>

              <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
                <button
                  type="submit"
                  className="check-btn"
                  disabled={isLoading}
                  style={{ flex: 1 }}
                >
                  {isLoading && <span className="loading-spinner"></span>}
                  {isLoading
                    ? "Working my magic..."
                    : "Let's cook something amazing! 🍳"}
                </button>
                <button
                  type="button"
                  className="check-btn"
                  onClick={() => {
                    setIngredients("");
                    setTime("");
                    setServings("");
                    setAvoid("");
                    searchRecipes();
                  }}
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #4CAF50, #45a049)",
                    border: "none",
                  }}
                >
                  Show Me Everything! 🎉
                </button>
              </div>
            </form>
          </div>

          {recipes.length > 0 && (
            <div className="recipe-list">
              {recipes.map((recipe) => (
                <div key={recipe.id} className="recipe-card">
                  <img
                    src={recipe.image}
                    alt={recipe.title}
                    className="recipe-image"
                  />
                  <h3 className="recipe-title">{recipe.title}</h3>
                  <p className="recipe-time">
                    {recipe.time || "⏰ Time not specified"}
                  </p>
                  <p className="recipe-summary">{recipe.summary}</p>
                  <div className="recipe-actions">
                    <button
                      className="recipe-btn"
                      onClick={() => viewRecipe(recipe)}
                    >
                      👁️ View Recipe
                    </button>
                    <button
                      className={`recipe-btn ${
                        favorites.some((fav) => fav.id === recipe.id)
                          ? "favorite-recipe-btn favorited"
                          : "favorite-recipe-btn"
                      }`}
                      onClick={() => toggleFavorite(recipe)}
                    >
                      {favorites.some((fav) => fav.id === recipe.id)
                        ? "⭐ Favorited"
                        : "⭐ Add to Favorites"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="recipe-favorites">
          <h3 className="favorites-title">⭐ Dana's Favorite Recipes</h3>
          {favorites.length === 0 ? (
            <div className="no-favorites">
              <div className="no-favorites-icon">🍽️</div>
              <h4>No Favorites Yet</h4>
              <p>Start searching for recipes and add your favorites!</p>
            </div>
          ) : (
            <div className="favorites-list">
              {favorites.map((recipe) => (
                <div key={recipe.id} className="favorite-recipe-card">
                  <img
                    src={recipe.image}
                    alt={recipe.title}
                    className="favorite-recipe-image"
                  />
                  <div className="favorite-recipe-info">
                    <h4 className="favorite-recipe-title">{recipe.title}</h4>
                    <p className="favorite-recipe-time">
                      {recipe.time || "⏰ Time not specified"}
                    </p>
                    <p className="favorite-recipe-servings">
                      {recipe.servings || "👥 Servings not specified"}
                    </p>
                    <p className="favorite-recipe-summary">{recipe.summary}</p>
                  </div>
                  <div className="recipe-actions">
                    <button
                      className="recipe-btn"
                      onClick={() => viewRecipe(recipe)}
                    >
                      👁️ View
                    </button>
                    <button
                      className="remove-favorite-btn"
                      onClick={() => removeFavorite(recipe.id)}
                    >
                      🗑️ Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Kitchen;
